/**
 * What to do when the Teamlio PF extension does not answer a ping.
 *
 * The same failure means two different things depending on who is looking at
 * it, and the right response is opposite in each case:
 *
 *   - On a developer's machine the extension is almost always installed and
 *     just needs reloading in chrome://extensions after an edit. Sending that
 *     person to the Web Store is useless -- the store copy is not the build
 *     they are working on, and installing it would actively get in the way.
 *     This is why the store tab is NOT opened on localhost.
 *
 *   - On a deployed site the person has no extension at all. A toast telling
 *     them to "reload it in chrome://extensions" describes a thing that is not
 *     there, and the install link sat inside that toast as plain text they
 *     could not even click.
 *
 * So the HOST decides, and on a deployed host the notice carries the listing
 * twice over: an automatic tab, and the link itself for the caller to render
 * as a button.
 *
 * The automatic tab is best effort and usually fails. It is asked for about
 * ten seconds after the click -- pingPfExtension waits 5s and retries once,
 * and when no extension is installed nothing ever answers, so both attempts
 * run to the full timeout. By then the browser no longer treats the click as
 * user activation (Chrome's window is a few seconds) and blocks the popup, so
 * the "redirect" did nothing at all and the button looked dead. The button in
 * the toast is the reliable path: clicking it is a fresh gesture, which the
 * popup blocker allows.
 *
 * Tested in src/utils/extensionInstall.test.ts.
 */
import { toast } from 'sonner';

// Published listing for the Teamlio PF Filing extension. It drives PF (ECR),
// ESI and TDS filing, so every one of those buttons leads here.
export const TEAMLIO_EXTENSION_WEBSTORE_URL =
  'https://chromewebstore.google.com/detail/teamlio-pf-filing/bnjnabfklgnpkgggiakfieoghbommopa';

const DEV_MESSAGE =
  'Teamlio PF extension not responding. On the dev build: reload it in ' +
  'chrome://extensions, then hard-refresh this tab (Ctrl+Shift+R). ' +
  `Don't have it at all? Install: ${TEAMLIO_EXTENSION_WEBSTORE_URL}`;

// Deliberately promises nothing about a tab: it is even odds the popup
// blocker ate it, and a toast saying "Opening the Chrome Web Store" while
// nothing opens is worse than no toast.
const LIVE_MESSAGE =
  'The Teamlio extension is needed for this. Install it from the Chrome Web ' +
  'Store, then come back to this tab and try again.';

// The developer's own machine, and nothing else. Matches the extension's own
// LOOPBACK_HOSTS in background.js, which draws the same line for the same
// reason.
const LOOPBACK_HOSTS = new Set(['localhost', '127.0.0.1', '[::1]', '::1']);

/**
 * True anywhere that is not the developer's own machine.
 *
 * This used to be `process.env.NODE_ENV === 'production'` and that was wrong:
 * the QA bundle does not report production, so qa.teamlio.ai -- a real site,
 * whose users have no extension and no unpacked copy to reload -- was shown
 * the developer's chrome://extensions message and never the store.
 *
 * The host is the honest signal, because the dev exception is not about how
 * the bundle was built. It exists for the UNPACKED extension, and that only
 * ever exists beside a dev server on loopback.
 */
export function isDeployedHost(hostname: string = window.location.hostname): boolean {
  return !LOOPBACK_HOSTS.has(hostname) && !hostname.endsWith('.localhost');
}

/** Where the person can be sent, if anywhere, plus what to tell them. */
export interface MissingExtensionNotice {
  message: string;
  /** The listing, for the caller to offer as something clickable. Null on
   *  localhost, where there is deliberately nothing to install. */
  installUrl: string | null;
}

type OpenFn = (url: string) => Window | null;

/** A new tab rather than this one: the person is part-way through a filing,
 *  and replacing the page they are on would lose it. noopener/noreferrer
 *  because the Web Store has no business holding a handle on the page a
 *  payroll session is open in. */
const openInNewTab: OpenFn = (url) => window.open(url, '_blank', 'noopener,noreferrer');

/**
 * Decide what a missing extension means here, and take the one action that
 * can be taken without the person's help. The `host` and `open` options exist
 * so a test can drive every path without a real site or a real window.
 */
export function extensionMissing(
  options: { host?: string; open?: OpenFn } = {},
): MissingExtensionNotice {
  if (!isDeployedHost(options.host)) return { message: DEV_MESSAGE, installUrl: null };

  const open = options.open ?? openInNewTab;
  // Best effort, and the return value is deliberately not consulted: the
  // button below is offered either way, so a popup that was blocked and a
  // popup that opened both leave the person a way through.
  open(TEAMLIO_EXTENSION_WEBSTORE_URL);
  return { message: LIVE_MESSAGE, installUrl: TEAMLIO_EXTENSION_WEBSTORE_URL };
}

/** The subset of sonner's options this uses. */
export interface ExtensionToastOptions {
  action?: { label: string; onClick: () => void };
}

/**
 * Show the missing-extension toast. One call, so the five buttons that need
 * it (EPFO login, ECR upload, approve, challan, and the TDS run) cannot drift
 * apart on how they word it or whether they offer the install link.
 */
export function showExtensionMissing(
  options: {
    host?: string;
    open?: OpenFn;
    notify?: (message: string, options?: ExtensionToastOptions) => void;
  } = {},
): void {
  const open = options.open ?? openInNewTab;
  const notify = options.notify ?? ((message, opts) => { toast.error(message, opts); });

  const notice = extensionMissing({ host: options.host, open });
  if (!notice.installUrl) {
    notify(notice.message);
    return;
  }
  const installUrl = notice.installUrl;
  notify(notice.message, {
    action: { label: 'Install', onClick: () => { open(installUrl); } },
  });
}
